// Конфигурация отрядов игры "Стратегикон"

import {isPlayerPolicy} from "../utils/factions/isPlayerPolicy";
import {POLICIES} from "../constants/policies";
import {
  POLICY_DESPOTISM_UNIT_DISCOUNT_D10,
  POLICY_DESPOTISM_UNIT_DISCOUNT_D12,
  POLICY_DESPOTISM_UNIT_DISCOUNT_D20,
  POLICY_DESPOTISM_UNIT_DISCOUNT_D6,
  POLICY_DESPOTISM_UNIT_DISCOUNT_D8
} from "./policy/despotism";
import {POLICY_BARBARISM_UNIT_D6_PRICE} from "./policy/barbarism";
import {FACTIONS} from "../constants/factions";
import {GIANTS_UNIT_MAX_SIZE} from "./factions/giants";

export const UNITS_SAME_TIME_COUNT_LIMIT = 4;

/**
 * Типы отрядов с их характеристиками
 * dice: количество граней кубика (определяет диапазон случайного урона)
 * maxSize: максимальный размер отряда
 * speed: скорость перемещения (максимальное количество ячеек за ход)
 * price: стоимость найма в монетах
 * winPoints: количество очков за уничтожение этого отряда
 */
export const UNIT_TYPES = {
  D6: {
    id: 'unit_d6',
    maxSize: 6,
    speed: 2,
    price: 2,
    sizePrice: 2,
    winPoints: 1,
    label: 'Отряд D6',
    description: 'Пехота'
  },
  D10: {
    id: 'unit_d10',
    maxSize: 10,
    speed: 2,
    price: 5,
    sizePrice: 2,
    winPoints: 2,
    label: 'Отряд D10',
    description: 'Тяжёлая пехота'
  },
  D20: {
    id: 'unit_d20',
    maxSize: 20,
    speed: 2,
    price: 10,
    sizePrice: 2,
    winPoints: 4,
    label: 'Отряд D20',
    description: 'Элитная пехота'
  },
  D4: {
    id: 'unit_d4',
    maxSize: 4,
    speed: 1,
    // Дешевле прежних 8/4: после замены грани +1 кости урона вторым нулем
    // (b704bb9d) лучший бросок стоявшей катапульты стал 0 вместо +1. Старые
    // записи партий проигрываются по прежним ценам: найм и докупка списывают
    // монеты по цене из своего действия (RECRUIT_UNIT / ADD_PENDING_RECRUITMENT_ACTION)
    price: 6,
    sizePrice: 3,
    winPoints: 3,
    label: 'Отряд D4',
    description: 'Катапульты'
  },
  D8: {
    id: 'unit_d8',
    maxSize: 8,
    speed: 4,
    price: 6,
    sizePrice: 3,
    winPoints: 2,
    label: 'Отряд D8',
    description: 'Лёгкая кавалерия'
  },
  D12: {
    id: 'unit_d12',
    maxSize: 12,
    speed: 4,
    price: 8,
    sizePrice: 3,
    winPoints: 3,
    label: 'Отряд D12',
    description: 'Тяжёлая кавалерия'
  },
};

// Пехота — d6, d10 и d20 (обычная, тяжёлая и элитная, см. description выше).
// Остальные типы пехотой не считаются: d4 — катапульты, d8 и d12 — кавалерия.
// Деление нужно правилам, которые действуют только на пехоту (например, +1 к
// размеру крысолюдов за дополнительное снабжение)
export const INFANTRY_UNIT_TYPE_IDS = [UNIT_TYPES.D6.id, UNIT_TYPES.D10.id, UNIT_TYPES.D20.id];

export const checkIfInfantryUnitType = (unitTypeId) => INFANTRY_UNIT_TYPE_IDS.includes(unitTypeId);

// Порядок типов в рядах жетонов: пехота д6, д10, д20, кавалерия д8, д12, катапульта д4
export const UNIT_TYPES_ROW_ORDER = [
  UNIT_TYPES.D6.id, UNIT_TYPES.D10.id, UNIT_TYPES.D20.id,
  UNIT_TYPES.D8.id, UNIT_TYPES.D12.id, UNIT_TYPES.D4.id,
];

// Получить конфигурацию типа отряда по ID
export const getUnitTypeConfig = (unitTypeId) => {
  return Object.values(UNIT_TYPES).find(u => u.id === unitTypeId);
};

// Получить цену отряда по типу
export const getUnitPrice = (unitTypeId, player) => {
  const config = getUnitTypeConfig(unitTypeId);
  let price = config.price;
  const originalPrice = price;

  if (isPlayerPolicy(player, POLICIES.DESPOTISM)) {
    switch (unitTypeId) {
      case UNIT_TYPES.D6.id:
        price -= POLICY_DESPOTISM_UNIT_DISCOUNT_D6;
        break;
      case UNIT_TYPES.D10.id:
        price -= POLICY_DESPOTISM_UNIT_DISCOUNT_D10;
        break;
      case UNIT_TYPES.D20.id:
        price -= POLICY_DESPOTISM_UNIT_DISCOUNT_D20;
        break;
      case UNIT_TYPES.D8.id:
        price -= POLICY_DESPOTISM_UNIT_DISCOUNT_D8;
        break;
      case UNIT_TYPES.D12.id:
        price -= POLICY_DESPOTISM_UNIT_DISCOUNT_D12;
        break;
      default:
    }
  }

  if (isPlayerPolicy(player, POLICIES.BARBARISM)) {
    if (unitTypeId === UNIT_TYPES.D6.id) {
      price = POLICY_BARBARISM_UNIT_D6_PRICE;
    }
  }

  const isDiscount = price < originalPrice;

  return {price: Math.max(0, price), isDiscount};
};

// Получить скорость отряда по типу
export const getUnitSpeed = (unitTypeId) => {
  const config = getUnitTypeConfig(unitTypeId);
  return config.speed;
};

// Получить максимальный размер отряда по типу
export const getUnitMaxSize = (unitTypeId) => {
  const config = getUnitTypeConfig(unitTypeId);
  return config.maxSize;
};

// Получить очки победы за уничтожение отряда
export const getUnitWinPoints = (unitTypeId) => {
  const config = getUnitTypeConfig(unitTypeId);
  return config.winPoints;
};

/**
 * Максимальный размер отряда с учетом фракции: у великанов он общий для любого
 * типа отряда (GIANTS_UNIT_MAX_SIZE) вместо лимита из конфига типа. Считать
 * его нужно везде, где отряду проставляется maxSize — не только при найме,
 * но и при смене типа (UNIT_UPGRADE), иначе фракционный лимит теряется
 */
export const getUnitMaxSizeForFaction = (unitTypeId, factionId) => {
  if (factionId === FACTIONS.GIANTS) {
    return GIANTS_UNIT_MAX_SIZE;
  }
  return getUnitTypeConfig(unitTypeId).maxSize;
};

// Создать новый отряд с указанными параметрами
export const createUnit = (unitId, hexKey, ownerId, type, factionId, size = 1) => {
  const maxSize = getUnitMaxSizeForFaction(type, factionId);
  return {
    id: unitId,
    hexKey,
    ownerId,
    type,
    factionId,
    size,
    maxSize,
    hasShotThisWarPhase: false,
    hasFoughtThisWarPhase: false,
    // Применял ли юнит способность любой фракции (попытка соблазнения у
    // Суккубов; установка/снятие ловушки, повреждение строения у Гоблинов) в
    // текущем этапе способностей фазы войны — один общий флаг на все
    // способности всех фракций, а не отдельный флаг на каждую. Внутри одной фракции способности
    // взаимоисключающие (см. add-goblins-faction design.md Decision 3 - для
    // Гоблинов), а разные фракции никогда не делят один и тот же юнит
    hasUsedAbilityThisWarPhase: false,
    // ID владельца, у которого юнит был отобран последним успешным
    // соблазнением (см. FACTION_SUCCUBES_SEDUCE_UNIT) - null, пока юнит не
    // захватывали ни разу. Используется вместо сравнения factionId для
    // определения "юнит захвачен" и цвета исходного владельца в
    // HexGridRenderer, т.к. сравнение по factionId ломается, когда несколько
    // игроков играют за одну фракцию (включая зеркальный матч Суккубов)
    capturedFromOwnerId: null,
  };
};

export default UNIT_TYPES;
